import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { Query } from 'react-apollo';
import { DropdownOption, utils, SignUpProps, Lab } from 'kb-shared';
import Honeybadger from '@honeybadger-io/js';

import * as redux from '../../../redux';
import { ReduxState } from '../../../redux';
import TextInput from '../../../components/TextInput/NewTextInput';
import SelectInput, { SelectWithOptions } from '../../../components/SelectInput';
import Button from '../../../components/Button/NewButton';
import { showErrorToast } from '../../../utilities/notificationUtils';

import {
  LabsQuery,
  VMarginWrapper,
  ButtonWrapper,
  TwoFieldWrapper,
  InputWrapperMargin,
  DisclaimerText,
  GenderInputWrapper,
  QUERY_LABS
} from '../../../components/SignUpFormShared';

const { signUpAppleUser } = redux.patient;

const {
  GENDER_INPUT_DEFAULTS,
  PRONOUN_INPUT_DEFAULTS,
  COUNTRIES_OPTIONS,
  US_COUNTRY_OPTION,
  isDOBValid,
  isDateValid,
  isLabValid,
  isInputValid,
  states
} = utils;

const FormContainer = styled.div`
  max-width: 400px;
  flex: 1;
`;

const FORM_ERRORS = {
  firstName: 'firstName',
  lastName: 'lastName',
  email: 'email',
  phone: 'phone',
  gender: 'gender',
  pronoun: 'pronoun',
  birthdate: 'birthdate',
  dob: 'dob',
  labId: 'labId',
  addressLine: 'addressLine',
  city: 'city',
  state: 'state',
  zipcode: 'zipCode',
  country: 'country',
  referralSource: 'referralSource'
};

const ERROR_MESSAGES = {
  firstName: 'Please enter a valid first name.',
  lastName: 'Please enter a valid last name.',
  email: 'Please enter a valid email.',
  phone: 'You must provide a phone number.',
  gender: 'You must select a gender.',
  pronoun: 'Please select a pronoun',
  dob: 'Patients must be 18 years or older to schedule a consult.',
  birthdate: 'Please enter a valid date.',
  labId: 'You must select a location.',
  addressLine: 'Please enter an address line',
  city: 'Please enter a city name',
  state: 'Please select a state',
  zipcode: 'Please enter a Zip Code',
  country: 'Please select a country',
  referralSource: 'Please let us know how you heard about us'
};

interface Props {
  onSuccess: () => void;
  preselectedLab: Lab | null;
}

type ERROR_FIELDS =
  | 'firstName'
  | 'lastName'
  | 'email'
  | 'phone'
  | 'gender'
  | 'pronoun'
  | 'birthdate'
  | 'dob'
  | 'labId'
  | 'addressLine'
  | 'city'
  | 'state'
  | 'zipcode'
  | 'country';

const SignUpWithApple = (props: Props) => {
  const disptach = useDispatch();
  const { loading, patient } = useSelector((state: ReduxState) => state.patient);

  const [firstName, setFirstName] = useState(''); //
  const [lastName, setLastName] = useState(''); //
  const [country, setCountry] = useState(US_COUNTRY_OPTION.value); //
  const [state, setState] = useState(''); //
  const [stateOption, setStateOption] = useState({ label: '', value: '' });
  const [city, setCity] = useState(''); //
  const [zipCode, setZipCode] = useState('');
  const [phone, setPhone] = useState('');
  const [birthdate, setBirthdate] = useState('');
  const [countryOption, setCountryOption] = useState(US_COUNTRY_OPTION);
  const [addressLine, setAddressLine] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [genderOption, setGenderOption] = useState(GENDER_INPUT_DEFAULTS[1]);
  const [gender, setGender] = useState(GENDER_INPUT_DEFAULTS[1].value);
  const [pronounOption, setPronounOption] = useState(PRONOUN_INPUT_DEFAULTS[1]);
  const [pronoun, setPronoum] = useState(PRONOUN_INPUT_DEFAULTS[1].value);
  const [patientLab, setPatientLab] = useState<Lab | null>(props.preselectedLab);
  const [errors, setErrors] = useState<ERROR_FIELDS[]>([]);
  const [referralSource, setReferralSource] = useState<string>('');

  useEffect(() => {
    const firstError = errors[0];
    const errorMessage = ERROR_MESSAGES[firstError];

    if (errorMessage) {
      showErrorToast(errorMessage);
    }
  }, [errors]);

  useEffect(() => {
    // This handles cases where user press to sign up already having an account
    if (patient.id) {
      props.onSuccess();
    }
  }, [patient.id, props]);

  const onUpdateCountry = (option: { label: string; value: string }) => {
    setCountry(option.value);
    setCountryOption(option);
  };

  const onUpdatePronoun = (option: { label: string; value: string }) => {
    setPronounOption(option);
    setPronoum(option.value);
  };

  const onUpdateReferralSource = (value: DropdownOption) => {
    setReferralSource(value.value);
  };

  const onUpdateGender = (option: { label: string; value: string }) => {
    setGenderOption(option);
    setGender(option.value);
  };

  const onUpdateLab = (value: Lab) => {
    setPatientLab(value);
  };

  const onUpdateState = (option: { label: string; value: string }) => {
    setStateOption(option);
    setState(option.value);
  };

  const isUS = (country: string) => country === 'United States' || country === 'USA';

  const isValidForm = (): boolean => {
    const formErrors: string[] = [];
    if (!isInputValid(firstName)) {
      formErrors.push(FORM_ERRORS.firstName);
    }
    if (!isInputValid(lastName)) {
      formErrors.push(FORM_ERRORS.lastName);
    }
    if (!isInputValid(country)) {
      formErrors.push(FORM_ERRORS.country);
    }
    if (!isInputValid(city)) {
      formErrors.push(FORM_ERRORS.city);
    }
    if (!isDateValid(birthdate)) {
      formErrors.push(FORM_ERRORS.birthdate);
    }
    if (!isDOBValid(birthdate)) {
      formErrors.push(FORM_ERRORS.dob);
    }
    if (!isInputValid(addressLine)) {
      formErrors.push(FORM_ERRORS.addressLine);
    }
    if (!isInputValid(gender)) {
      formErrors.push(FORM_ERRORS.gender);
    }
    if (isUS(country) && !isInputValid(state)) {
      formErrors.push(FORM_ERRORS.state);
    }
    if (isUS(country) && !isInputValid(zipCode)) {
      formErrors.push(FORM_ERRORS.zipcode);
    }
    if (!referralSource || !isInputValid(referralSource)) {
      formErrors.push(FORM_ERRORS.referralSource);
    }
    // @ts-ignore
    if (!isLabValid(patientLab)) {
      formErrors.push(FORM_ERRORS.labId);
    }

    setErrors([...formErrors]);
    return formErrors.length === 0;
  };

  const handleSignup = async () => {
    if (!isValidForm()) {
      return;
    }

    if (!patientLab) {
      return;
    }

    const signUpData: SignUpProps = {
      firstName,
      lastName,
      email: patient.email,
      phone,
      gender: gender,
      pronoun: pronoun,
      birthdate: birthdate,
      labId: parseInt(patientLab.id),
      address: {
        address1: addressLine,
        address2: addressLine2,
        city: city,
        state: state,
        zipcode: zipCode,
        country: country
      },
      referralSource
    };

    try {
      await disptach(signUpAppleUser(signUpData));
      props.onSuccess();
    } catch (e) {
      Honeybadger.notify(e);
      showErrorToast('Something went wront, please try again later.');
    }
  };

  if (loading) {
    return null;
  }

  return (
    <FormContainer>
      <VMarginWrapper>
        <TwoFieldWrapper>
          <InputWrapperMargin>
            <TextInput
              name="firstName"
              label="FIRST NAME"
              value={firstName}
              onChange={event => setFirstName(event.target.value)}
              error={errors.includes(FORM_ERRORS.firstName)}
              autoComplete="given-name"
              spellCheck="false"
              placeholder="First name"
            />
          </InputWrapperMargin>
          <InputWrapperMargin>
            <TextInput
              name="lastName"
              label="LAST NAME"
              value={lastName}
              onChange={event => setLastName(event.target.value)}
              error={errors.includes(FORM_ERRORS.lastName)}
              autoComplete="family-name"
              spellCheck="false"
              placeholder="Last name"
            />
          </InputWrapperMargin>
        </TwoFieldWrapper>
        <DisclaimerText>
          Be sure to enter your full legal name for your medical record.
        </DisclaimerText>
      </VMarginWrapper>

      <VMarginWrapper>
        <SelectInput
          name="country"
          label="COUNTRY"
          extractTitle={item => (item ? item.label : '')}
          value={country}
          placeholder="Select country"
          selectedOption={countryOption}
          onSelect={onUpdateCountry}
          options={COUNTRIES_OPTIONS}
        />
      </VMarginWrapper>

      <TwoFieldWrapper>
        <InputWrapperMargin>
          <TextInput
            name="addressLine"
            label="ADDRESS LINE 1"
            value={addressLine}
            onChange={event => setAddressLine(event.target.value)}
            placeholder="Address"
          />
        </InputWrapperMargin>
        <InputWrapperMargin>
          <TextInput
            name="addressLine2"
            label="ADDRESS LINE 2"
            value={addressLine2}
            onChange={event => setAddressLine2(event.target.value)}
            placeholder={'Address'}
          />
        </InputWrapperMargin>
      </TwoFieldWrapper>

      {isUS(country) ? (
        <VMarginWrapper>
          <SelectInput
            name="state"
            label="STATE"
            type="select"
            placeholder="Select state"
            selectedOption={stateOption}
            extractTitle={item => (item ? item.label : '')}
            options={states}
            onSelect={onUpdateState}
          />
        </VMarginWrapper>
      ) : (
        <VMarginWrapper>
          <TextInput
            name="state"
            label="STATE"
            value={state}
            placeholder="State"
            // @ts-ignore
            onChange={event => setState(event.target.value)}
          />
        </VMarginWrapper>
      )}

      <TwoFieldWrapper>
        <InputWrapperMargin>
          <TextInput
            name="city"
            label="CITY"
            placeholder="City"
            value={city}
            onChange={event => setCity(event.target.value)}
          />
        </InputWrapperMargin>
        {isUS(country) ? (
          <InputWrapperMargin>
            <TextInput
              name="zipCode"
              label="ZIP"
              value={zipCode}
              placeholder="55555"
              autoComplete="postal-code"
              inputType="zip"
              onChange={event => setZipCode(event.target.value)}
            />
          </InputWrapperMargin>
        ) : (
          <InputWrapperMargin>
            <TextInput
              name="zipCode"
              label="ZIP"
              value={zipCode}
              placeholder="ZIP"
              onChange={event => setZipCode(event.target.value)}
            />
          </InputWrapperMargin>
        )}
      </TwoFieldWrapper>

      <VMarginWrapper>
        <TwoFieldWrapper>
          <InputWrapperMargin>
            <TextInput
              inputType={isUS(country) ? 'phone' : 'text'}
              name="phone"
              label="PHONE NUMBER"
              value={phone}
              onChange={event => setPhone(event.target.value)}
              autoComplete="tel"
              inputMode="tel"
              placeholder="(+XXXX)Phone Number"
            />
          </InputWrapperMargin>
          <InputWrapperMargin>
            <TextInput
              inputType="date"
              name="birthdate"
              label="BIRTHDATE"
              value={birthdate}
              onChange={event => setBirthdate(event.target.value)}
              autoComplete="bday"
            />
          </InputWrapperMargin>
        </TwoFieldWrapper>
      </VMarginWrapper>

      <VMarginWrapper>
        <TwoFieldWrapper>
          <GenderInputWrapper>
            <SelectInput
              label="SEX AT BIRTH"
              extractTitle={item => (item ? item.label : '')}
              placeholder="Select"
              selectedOption={genderOption}
              onSelect={onUpdateGender}
              options={GENDER_INPUT_DEFAULTS}
            />
          </GenderInputWrapper>
          <InputWrapperMargin>
            <SelectInput
              label="PRONOUN"
              extractTitle={item => (item ? item.label : '')}
              placeholder="Select"
              selectedOption={pronounOption}
              onSelect={onUpdatePronoun}
              options={PRONOUN_INPUT_DEFAULTS}
            />
          </InputWrapperMargin>
        </TwoFieldWrapper>
      </VMarginWrapper>

      <VMarginWrapper>
        <Query query={QUERY_LABS}>
          {({ data }: LabsQuery) => {
            const alphabetically = () => {
              // @ts-ignore
              return function(a, b) {
                // equal items sort equally
                if (a.name === b.name) {
                  return 0;
                }
                // Other sort after anything else
                else if (a.name === 'Other') {
                  return 1;
                } else if (b.name === 'Other') {
                  return -1;
                }
                // otherwise lowest sorts first
                else {
                  return a.name < b.name ? -1 : 1;
                }
              };
            };

            const sortedLabs = data?.labs.sort(alphabetically());

            return (
              <SelectInput
                label="KINDBODY HOME LOCATION"
                extractTitle={lab => (lab ? lab.name : '')}
                placeholder={'Select city'}
                selectedOption={patientLab}
                onSelect={onUpdateLab}
                options={(data && sortedLabs) || []}
              />
            );
          }}
        </Query>
      </VMarginWrapper>

      <VMarginWrapper>
        <SelectWithOptions
          label="HOW DID YOU HEAR ABOUT US?"
          value={referralSource}
          placeholder={'Select one option'}
          lookup_key="patient_referral_source"
          onSelect={onUpdateReferralSource}
        />
      </VMarginWrapper>

      <ButtonWrapper>
        <Button
          text="SUBMIT YOUR DETAILS"
          onClick={handleSignup}
          // loading={this.props.loading}
        />
      </ButtonWrapper>
    </FormContainer>
  );
};

export default SignUpWithApple;
